
import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import { Plus, Trash2 } from 'lucide-react';
import { SocialPlatform } from '@/types/socialPlatform';

interface CountrySocialMediaFieldsProps {
  socialPlatforms: SocialPlatform[];
  onChange: (platforms: SocialPlatform[]) => void;
}

export const CountrySocialMediaFields: React.FC<CountrySocialMediaFieldsProps> = ({ 
  socialPlatforms, 
  onChange 
}) => {
  const defaultPlatforms = [
    'facebook',
    'instagram',
    'x',
    'tiktok',
    'youtube'
  ];

  // Helper function to validate URL
  const isValidUrl = (url: string): boolean => {
    try {
      new URL(url);
      return true;
    } catch {
      return false;
    }
  };

  // Helper function to ensure URL has correct protocol
  const ensureHttps = (url: string): string => {
    if (!url) return '';
    
    if (!url.startsWith('http://') && !url.startsWith('https://')) {
      return `https://${url}`;
    }
    return url;
  };

  // Add a new platform
  const handleAddPlatform = () => {
    const newPlatform: SocialPlatform = {
      platform: '',
      handle: '',
      url: '',
      active: true
    };
    
    onChange([...socialPlatforms, newPlatform]);
  };

  // Remove a platform
  const handleRemovePlatform = (index: number) => {
    const updated = [...socialPlatforms];
    updated.splice(index, 1);
    onChange(updated);
  };

  // Update a platform's properties
  const handleUpdatePlatform = (index: number, field: keyof SocialPlatform, value: string | boolean) => {
    const updated = [...socialPlatforms];
    updated[index] = { ...updated[index], [field]: value };
    
    // If updating the platform, suggest a URL based on platform and handle
    if (field === 'platform' || field === 'handle') {
      const platform = field === 'platform' ? value as string : updated[index].platform;
      const handle = field === 'handle' ? value as string : updated[index].handle;
      
      if (platform && handle) {
        switch (platform) {
          case 'facebook':
            updated[index].url = `https://facebook.com/${handle}`;
            break;
          case 'instagram':
            updated[index].url = `https://instagram.com/${handle}`;
            break;
          case 'x':
            updated[index].url = `https://x.com/${handle}`;
            break;
          case 'tiktok':
            updated[index].url = `https://tiktok.com/@${handle}`;
            break;
          case 'youtube':
            // YouTube can be a channel or user
            if (handle.startsWith('@')) {
              updated[index].url = `https://youtube.com/${handle}`;
            } else {
              updated[index].url = `https://youtube.com/c/${handle}`;
            }
            break;
        }
      }
    }
    
    // If updating the URL directly, ensure it has https://
    if (field === 'url') {
      updated[index].url = ensureHttps(value as string);
    }
    
    onChange(updated);
  };

  return (
    <div className="space-y-4 mt-4">
      <div className="flex justify-between items-center">
        <h3 className="text-lg font-semibold">Social Media Platforms</h3>
        <Button 
          onClick={handleAddPlatform} 
          size="sm" 
          className="flex items-center gap-1"
        >
          <Plus className="h-4 w-4" /> Add Platform
        </Button>
      </div>
      
      <Separator />
      
      {socialPlatforms.length === 0 ? (
        <div className="text-center py-4 text-muted-foreground">
          No social platforms configured yet. Add a platform to get started.
        </div>
      ) : (
        <div className="space-y-6">
          {socialPlatforms.map((platform, index) => (
            <div key={index} className="p-4 border rounded-lg bg-gray-50 dark:bg-gray-900">
              <div className="flex justify-between items-center mb-4">
                <div className="flex items-center gap-2">
                  <Label>Platform</Label>
                  <select
                    value={platform.platform}
                    onChange={(e) => handleUpdatePlatform(index, 'platform', e.target.value)}
                    className="border rounded px-2 py-1 bg-white dark:bg-gray-800 text-sm"
                  >
                    <option value="">Select platform</option>
                    {defaultPlatforms.map((p) => (
                      <option key={p} value={p}>{p.charAt(0).toUpperCase() + p.slice(1)}</option>
                    ))}
                    <option value="other">Other</option>
                  </select>
                  
                  {platform.platform === 'other' && (
                    <Input
                      placeholder="Custom platform name"
                      value={platform.platform === 'other' ? '' : platform.platform}
                      onChange={(e) => handleUpdatePlatform(index, 'platform', e.target.value)}
                      className="w-40"
                    />
                  )}
                </div>
                <Button 
                  variant="ghost" 
                  size="sm" 
                  onClick={() => handleRemovePlatform(index)}
                >
                  <Trash2 className="h-4 w-4 text-red-500" />
                </Button>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor={`handle-${index}`}>Handle/Username</Label>
                  <Input
                    id={`handle-${index}`}
                    value={platform.handle}
                    onChange={(e) => handleUpdatePlatform(index, 'handle', e.target.value)}
                    placeholder="e.g. nationcard"
                  />
                </div>
                
                <div>
                  <Label htmlFor={`url-${index}`}>URL</Label>
                  <Input
                    id={`url-${index}`}
                    value={platform.url}
                    onChange={(e) => handleUpdatePlatform(index, 'url', e.target.value)}
                    placeholder="e.g. https://facebook.com/nationcard"
                    className={!isValidUrl(platform.url) && platform.url ? "border-red-300" : ""}
                  />
                  {!isValidUrl(platform.url) && platform.url && (
                    <p className="text-xs text-red-500 mt-1">
                      Please enter a valid URL with https://
                    </p>
                  )}
                </div>
              </div>
              
              <div className="mt-4 flex items-center space-x-2">
                <Switch
                  checked={platform.active}
                  onCheckedChange={(checked) => handleUpdatePlatform(index, 'active', checked)}
                  id={`active-${index}`}
                />
                <Label htmlFor={`active-${index}`}>Active</Label>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
